<template>
    <open-card :open="cardOpen" card-class="overflow-unset">
        <div class="d-flex flex-row w-100" style="height: min-content;">
            <v-select ref="search"
                      class="flex-grow-1"
                      placeholder="Search something"
                      append-to-body clearSearchOnSelect
                      input-id="vSearch"
                      :options="options"
                      :filterBy="filter"
                      @option:selected="selected"
                      @open="cardOpen=false"></v-select>
            <mini-button v-if="!noSearchBtn" :loading="searchLoading" variant="light" class="ml-1 search-btn"
                         @btnClick="emitSearch">
                <i class="fa-solid fa-magnifying-glass"></i>
            </mini-button>
            <b-button v-if="!disablePop && !pin" class="ml-1 position-relative" variant="light" style="width: 50px"
                      @click="cardOpen=!cardOpen">
                <i v-if="cardOpen" class="fa-solid fa-up-long"></i>
                <i v-else class="fa-solid fa-down-long"></i>
                <b-badge v-if="filterCount>0" class="position-absolute left bottom" style="height: min-content;">
                    {{ filterCount }}
                </b-badge>
            </b-button>
            <key-help class="h-100 ml-1" :info="keyInfo"></key-help>
        </div>

        <template v-slot:[formPosition]>
            <div class="w-100 d-flex flex-row" :class="disablePop || 'p-2'">
                <div class="flex-grow-1">
                    <template v-for="item of Object.keys(filters)">
                        <div :key="item" class="d-flex flex-row mt-1">
                            <b-button variant="light">
                                {{ $t(filters[item].field.label) }}
                            </b-button>
                            <b-button variant="light" class="ml-1">
                                {{ getOpLabel(filters[item].op.label, filters[item].field.originalType) }}
                            </b-button>
                            <template v-if="!filters[item].op.noValue">
                                <template v-if="filters[item].field.originalType === 'Date'">
                                    <b-button :variant="filters[item].isRelativeDate?'info':'light'" class="ml-1"
                                              @click="triggerRelativeDate(item)">
                                        R
                                    </b-button>
                                </template>
                                <template v-if="filters[item].isRelativeDate">
                                    <template v-if="filters[item].op.name==='Between'">
                                        <mini-relative-date-range :value.sync="filterForm[item]" to-json
                                                                  @change="emit"></mini-relative-date-range>
                                    </template>
                                    <template v-else>
                                        <mini-relative-date :value.sync="filterForm[item]" to-json
                                                            @change="emit"></mini-relative-date>
                                    </template>
                                </template>
                                <template v-else-if="filters[item].op.name==='Between'">
                                    <mini-date-range class="flex-grow-1 ml-1 mr-1"
                                                     to-json
                                                     :value.sync="filterForm[item]"
                                                     @change="emit"></mini-date-range>
                                </template>
                                <template v-else>
                                    <mini-input :ref="item"
                                                class="flex-grow-1 ml-1 mr-1"
                                                :type="inputType(filters[item])"
                                                :value.sync="filterForm[item]"
                                                without-label multi-to-json
                                                :options="filters[item].field.options"
                                                :options-from-dict="filters[item].field.optionsFromDict"
                                                @change="emit"
                                                @enter="emitSearchDelay"></mini-input>
                                </template>
                            </template>
                            <b-button variant="light" class="ml-auto" @click="removeFilter(item)">
                                <i class="fa-solid fa-xmark"></i>
                            </b-button>
                        </div>
                    </template>
                </div>
                <b-button v-if="!disablePop && Object.keys(filters).length>0" variant="light" class="ml-1"
                          @click="pin=!pin">
                    <i v-if="pin" class="fa-solid fa-thumbtack-slash"></i>
                    <i v-else class="fa-solid fa-thumbtack"></i>
                </b-button>
            </div>
        </template>
    </open-card>
</template>

<script>
import MiniInput from "@/components/common/inputs/MiniInput.vue";
import MiniButton from "@/components/common/buttons/MiniButton.vue";
import {match} from "pinyin-pro";
import OpenCard from "@/components/common/assistant/OpenCard.vue";
import MiniDateRange from "@/components/common/inputs/MiniDateRange.vue";
import MiniRelativeDate from "@/components/common/inputs/MiniRelativeDate.vue";
import MiniRelativeDateRange from "@/components/common/inputs/MiniRelativeDateRange.vue";
import emCommonUtilMixin from "@/mixin/em/common/stuffs/utils/emCommonUtilMixin";
import KeyHelp from "@/components/common/display/KeyHelp.vue";

const OP = [
    {name: 'In', label: '包含/In', onlyType: 'select'},
    {name: 'Like', label: '包含/Like', onlyOrigin: 'String'},
    {name: 'Between', label: '介于之间', onlyOrigin: 'Date'},
    {name: 'Eq', label: '等于', notOrigin: 'Date', notType: 'select'},
    {name: 'Not', label: '不等于', notOrigin: 'Date', notType: 'select'},
    {name: 'Gt', label: (origin) => origin === 'Date' ? '晚于' : '大于', notOrigin: 'String', notType: 'select'},
    {name: 'Lt', label: (origin) => origin === 'Date' ? '早于' : '小于', notOrigin: 'String', notType: 'select'},
    {name: 'Gte', label: (origin) => origin === 'Date' ? '不早于' : '大于等于', notOrigin: 'String', notType: 'select'},
    {name: 'Lte', label: (origin) => origin === 'Date' ? '不晚于' : '小于等于', notOrigin: 'String', notType: 'select'},
    {name: 'Reg', label: '正则匹配', onlyOrigin: 'String'},
    {name: 'IsNull', label: '为空', noValue: true},
    {name: 'NotNull', label: '不为空', noValue: true},
];

export default {
    name: "TableSearch",
    components: {KeyHelp, MiniRelativeDateRange, MiniRelativeDate, MiniDateRange, OpenCard, MiniButton, MiniInput},
    mixins: [emCommonUtilMixin],
    props: {
        fields: Array,
        searchLoading: Boolean,
        noSearchBtn: Boolean,
        initForm: Object,
        disablePop: Boolean,
        showInvisible: Boolean,
    },
    data() {
        return {
            notAccept: new Set(['image']),
            filters: {},
            filterForm: {},

            cardOpen: false,
            pin: false,
            lastFocusInput: '',

            keyInfo: [
                {key: '/', desc: 'To Open Search'},
                {key: 'Tab', desc: 'To Focus last input'},
                {key: 'Enter', desc: 'To Select search Item or Do search'},
            ]
        }
    },
    computed: {
        options() {
            let tempList = [];
            if (this.fields) {
                for (let item of this.fields) {
                    if (item.visible || this.showInvisible) {
                        let type = item.type;
                        if (this.notAccept.has(type)) {
                            continue;
                        }
                        let transLabel = this.$t(item.label);
                        for (let op of OP) {
                            if (op.onlyOrigin && op.onlyOrigin !== item.originalType
                                || op.onlyType && op.onlyType !== type
                                || op.notOrigin && op.notOrigin === item.originalType
                                || op.notType && op.notType === type) {
                                continue;
                            }
                            tempList.push({
                                label: transLabel + ' ' + this.getOpLabel(op.label, item.originalType),
                                value: item,
                                operator: op
                            })
                        }
                    }
                }
            }
            return tempList;
        },
        formPosition() {
            return this.disablePop || this.pin ? 'outer' : 'card';
        },
        filterCount() {
            return Object.keys(this.filters).length;
        },
    },
    watch: {
        filterForm: {
            handler() {
                if (this.initForm) {
                    for (let key of Object.keys(this.initForm)) {
                        for (let op of OP) {
                            let index = key.lastIndexOf(op.name);
                            if (index > 0) {
                                let k = key.substring(0, index);
                                let got = false;
                                for (let item of this.options) {
                                    if (item.value.key === k) {
                                        if (key in this.filters) {
                                            return;
                                        }
                                        let temp = {
                                            field: item.value,
                                            op: op
                                        };
                                        let value = this.initForm[key];
                                        if (typeof value === 'string' && value.startsWith('{')) {
                                            value = JSON.parse(value);
                                        }
                                        if (value && (value.from && value.from.relative || value.relative)) {
                                            temp.isRelativeDate = true;
                                        }
                                        this.$set(this.filters, key, temp);
                                        this.$set(this.filterForm, key, value);
                                        got = true;
                                        break;
                                    }
                                }
                                if (got) {
                                    break;
                                }
                            }
                        }
                    }
                }
            },
            immediate: true,
            deep: true
        }
    },
    mounted() {
        document.addEventListener('keydown', this.whenKeyDown);
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.whenKeyDown);
    },
    methods: {
        filter(option, label, search) {
            return match(label, search) != null;
        },
        selected(item) {
            this.cardOpen = true;
            let key = item.value.key + item.operator.name;
            if (!(key in this.filters)) {
                this.$set(this.filters, key, {
                    field: item.value,
                    op: item.operator
                });
            }
            if (item.operator.noValue) {
                this.$set(this.filterForm, key, '');
            }
            this.emit();
            this.focusInput(key);
        },
        inputType(item) {
            if (item.op.name === 'In') {
                return 'select-multi';
            } else {
                return item.field.type;
            }
        },
        emit() {
            this.$emit('change', this.filterForm);
        },
        removeFilter(key) {
            this.$delete(this.filters, key);
            this.$delete(this.filterForm, key);
            this.emit();
        },
        emitSearch() {
            this.cardOpen = false;
            this.$emit("search", this.filterForm);
        },
        emitSearchDelay() {
            this.$nextTick(() => this.emitSearch());
        },
        getOpLabel(label, origin) {
            if (label instanceof Function) {
                return label(origin);
            }
            return label;
        },

        triggerRelativeDate(key) {
            this.$set(this.filters[key], 'isRelativeDate', !this.filters[key].isRelativeDate)
        },

        focusInput(ref, next = false) {
            this.$nextTick(() => {
                let keys = Object.keys(this.filters);
                for (let i = 0; i < keys.length; i++) {
                    let key = keys[i];
                    if (key === ref) {
                        let item = this.filters[key];
                        if (item.op.noValue || next) {
                            let got = false, j = i + 1;
                            while (!got) {
                                if (j >= keys.length) {
                                    j = 0;
                                }
                                let temp = this.filters[keys[j]];
                                if (!temp.op.noValue) {
                                    got = true;
                                    ref = keys[j];
                                }
                                if (j === i) {
                                    break;
                                }
                                j++;
                            }
                        }
                        break;
                    }
                }
                let el = this.getRefEl(ref);
                el && el.focus();
                this.lastFocusInput = ref;
            });
        },
        focusSearch() {
            let el = document.getElementById('vSearch');
            el && el.focus();
        },
        whenKeyDown(event) {
            if (event) {
                if (event.code === 'Slash') {
                    this.cardOpen = false;
                    this.focusSearch();
                    event.preventDefault();
                } else if (event.code === 'Tab') {
                    if (this.filterCount > 0) {
                        if (this.cardOpen) {
                            this.focusInput(this.lastFocusInput, true);
                        } else {
                            this.cardOpen = true;
                            this.focusInput(this.lastFocusInput);
                        }
                    } else {
                        this.focusSearch();
                    }
                    event.preventDefault();
                }
            }
        },
    }
}
</script>

<style scoped>
.search-btn {
    width: 60px;
}
</style>
<style>
.overflow-unset {
    overflow: unset !important;
}
</style>
